/**
 * Turning a recording into a sit of a given length.
 *
 * This is the heart of the product and it is deliberately pure: no audio, no network,
 * no clock. Given the same recording, target and cadence it always returns the same
 * plan, which makes it cheap to test and safe to run during browsing to preview
 * a hundred candidate sessions at once.
 *
 * The rule it enforces everywhere: speech durations are constants. Only gaps move.
 */

import type {
  Cadence, DurationCue, Gap, Recording, ReviewedPhaseVariant, SessionPlan, SpeechIsland, Timed,
} from '../types';
import { CUE_REVIEWS, LONG_LINKS, SHORT_PAUSE_SEC } from './flow-review.ts';

/**
 * The comparable shape of a plan. The app writes this into the trace header (`plan`)
 * and qa/sit applies it to every re-plan, so both sides are always the same function.
 *
 * Field meanings match `PlanFingerprint` in qa/inspector/types.ts. Declared here rather
 * than imported: `src/` has no Node types and must not depend on the inspector.
 */
export interface PlanFingerprint {
  /** Speech segments in the timeline — the same count the old `islands` header field holds. */
  speech: number;
  /** Tone segments in the timeline — the old `gaps` header field. */
  gaps: number;
  /** Rounded to the millisecond. */
  actualSec: number;
  /** Kept island indices in play order, each once. */
  islands: number[];
  /** [island, srcStart, srcEnd] per speech segment, in ms-rounded seconds. */
  speechSpans: Array<[number, number, number]>;
  /** Played length of each tone segment, in ms-rounded seconds. */
  gapSec: number[];
}

function roundMs(sec: number): number {
  return Math.round(sec * 1000) / 1000;
}

/**
 * Reduce a plan to the fields a later reconstruction can compare.
 *
 * A whole-island speech segment has no `srcStart`/`srcEnd` on the timeline entry —
 * those mean a cue trim — so the bounds come from `recording.speech[island]`. A
 * cue-trimmed island therefore appears once in `islands` and twice in `speechSpans`.
 */
export function planFingerprint(plan: SessionPlan, recording: Recording): PlanFingerprint {
  const speechSegs = plan.timeline.filter((seg): seg is Extract<Timed, { kind: 'speech' }> =>
    seg.kind === 'speech');
  const toneSegs = plan.timeline.filter((seg): seg is Extract<Timed, { kind: 'tone' }> =>
    seg.kind === 'tone');
  const islands: number[] = [];
  for (const seg of speechSegs) {
    if (!islands.includes(seg.island)) islands.push(seg.island);
  }
  return {
    speech: speechSegs.length,
    gaps: toneSegs.length,
    actualSec: roundMs(plan.actualSec),
    islands,
    speechSpans: speechSegs.map((seg) => {
      const island = recording.speech[seg.island];
      const srcStart = seg.srcStart ?? island.srcStart;
      const srcEnd = seg.srcEnd ?? island.srcEnd;
      return [seg.island, roundMs(srcStart), roundMs(srcEnd)];
    }),
    gapSec: toneSegs.map((seg) => roundMs(seg.endSec - seg.startSec)),
  };
}

/** How much sitting we aim for between reminders, per cadence. */
const REMINDER_INTERVAL_SEC: Record<Cadence, number> = {
  opening: Infinity,   // none at all: he sets you going and leaves you alone
  occasional: 12 * 60,
  frequent: 5 * 60,
};

export interface PlanOptions {
  /** Seconds of tolerance around the target before we call it a miss. */
  toleranceSec?: number;
  /** Desired spoken fraction, satisfied only through explicitly reviewed phase variants. */
  desiredSpeechRatio?: number;
}

export interface SessionReach {
  minSec: number;
  maxSec: number;
}

export class UnreachableError extends Error {
  readonly recordingId: string;
  readonly targetSec: number;
  readonly reach: { minSec: number; maxSec: number };

  // Plain field assignment rather than parameter properties: this module is imported
  // directly by tools/plan.mjs under Node's type-stripping, which cannot rewrite them.
  constructor(
    recordingId: string,
    targetSec: number,
    reach: { minSec: number; maxSec: number },
  ) {
    super(
      `${recordingId} cannot reach ${Math.round(targetSec / 60)} min ` +
      `(reaches ${Math.round(reach.minSec / 60)}–${Math.round(reach.maxSec / 60)})`,
    );
    this.name = 'UnreachableError';
    this.recordingId = recordingId;
    this.targetSec = targetSec;
    this.reach = reach;
  }
}

// ---------------------------------------------------------------- cadence

/**
 * The island that comes straight out of the longest silence.
 *
 * `role` is assigned by position in `segment.py` — first island `opening`, last island
 * `closing`, everything else `reminder` — and nothing reads the words. So on 26 May 2017
 * the island saying "we're very close to the end of the meditation... knowing the gong to
 * end the meditation" is a `reminder`, and the island called `closing` is the opening of
 * that evening's Dhamma talk. A 25-minute sit on 2026-09-12 dropped the first and played
 * the second, and the listener stopped it 33 seconds into a story about a terrorist attack.
 *
 * The longest gap is a proxy for "the sitting period", and it is a poor one — in
 * `Guided Metta Meditation | 26 May 2015` the longest gap is 93.9 s after island 0 while
 * the meditation itself is twenty-three continuously guided minutes in island 3. It is
 * used anyway because of how it fails: this only ever *adds* an island to the keep set.
 * When the proxy is wrong the cost is one short island nobody needed; when it is right it
 * is the difference between coming out of a sit and being handed a news item. Measured
 * over every target and cadence: it restores a genuine coming-out instruction in 201 of
 * 2,049 combinations.
 *
 * This is not the fix. The fix is a signed classifier reading transitions rather than
 * guessing from durations — see docs/COMPOSITION-AUDIT.md. This is what can be done
 * without deleting any speech or withholding anything while that is built.
 */
export function comingOutIsland(rec: Recording): number | null {
  if (rec.gaps.length === 0) return null;
  let longest = rec.gaps[0];
  for (const gap of rec.gaps) if (gap.nativeSec > longest.nativeSec) longest = gap;
  const index = longest.afterIsland + 1;
  return index > 0 && index < rec.speech.length ? index : null;
}

/**
 * Choose which speech islands survive at this cadence.
 *
 * Opening and closing are never dropped — they are the ritual boundaries of the sit.
 * Neither is the island that comes out of the longest silence, for the reason above.
 * Mid-session reminders are selected for even spacing rather than in file order, so a
 * recording that happens to cluster three reminders in its first five minutes still
 * yields a sit that feels evenly held.
 */
/** A duration threshold only protects continuity; it never authorizes a speech edit. */
export function protectedTransitions(rec: Recording): Set<number> {
  const linked = new Set(rec.gaps
    .filter((gap) => gap.afterIsland >= 0 && gap.afterIsland + 1 < rec.speech.length
      && gap.nativeSec <= SHORT_PAUSE_SEC)
    .map((gap) => gap.afterIsland));
  for (const [left, leftEnd, rightStart, nativeSec] of LONG_LINKS[rec.id] ?? []) {
    const gap = rec.gaps.find((g) => g.afterIsland === left);
    if (rec.speech[left]?.srcEnd !== leftEnd || rec.speech[left + 1]?.srcStart !== rightStart
        || gap?.nativeSec !== nativeSec) {
      throw new InvalidEditError(rec.id, `stale flow review at island ${left}`);
    }
    linked.add(left);
  }
  for (const d of rec.linkDecisions ?? []) {
    const review = d.review;
    if (d.schema !== 'link-v1' || d.verdict !== 'independent'
        || review?.status !== 'approved' || review.by !== 'quorum:link-v1' || !review.decisionId
        || ![review.inputSha256, review.rubricSha256, review.manifestSha256].every(h => /^[a-f0-9]{64}$/.test(h))
        || review.votes?.length !== 2
        || !review.votes.every(v => v.verdict === 'independent')
        || new Set(review.votes.map(v => v.by)).size !== 2
        || !review.votes.some(v => v.by === 'codex:workhorse') || !review.votes.some(v => v.by === 'grok')) continue;
    const left = d.afterIsland, gap = rec.gaps.find(g => g.afterIsland === left);
    if (!Number.isInteger(left) || !rec.speech[left + 1]
        || rec.speech[left]?.srcEnd !== d.leftEnd || rec.speech[left + 1].srcStart !== d.rightStart
        || gap?.nativeSec !== d.nativeSec) {
      throw new InvalidEditError(rec.id, `stale link review at island ${left}`);
    }
    // Conflicting or duplicate records cannot silently override each other.
    if (rec.linkDecisions?.filter(other => other.afterIsland === left).length === 1) linked.delete(left);
  }
  return linked;
}

function retainLinkedPassages(rec: Recording, keep: Set<number>): number[] {
  const linked = protectedTransitions(rec);
  // A group is one continuous instruction. Selecting any part retains its context in
  // both directions, including when a cadence pick lands in the middle of the group.
  let start = 0;
  for (let end = 0; end < rec.speech.length; end++) {
    if (linked.has(end)) continue;
    if (Array.from({ length: end - start + 1 }, (_, i) => start + i).some((i) => keep.has(i))) {
      for (let i = start; i <= end; i++) keep.add(i);
    }
    start = end + 1;
  }
  return [...keep].sort((a, b) => a - b);
}

export function selectIslands(rec: Recording, targetSec: number, cadence: Cadence): number[] {
  const keep = new Set<number>();
  rec.speech.forEach((isl, i) => {
    if (isl.role !== 'reminder') keep.add(i);
  });
  // Pinned before the cadence budget is spent, and deliberately not counted against it:
  // coming out of the sit is a boundary of the session, not one of its reminders.
  const comingOut = comingOutIsland(rec);
  if (comingOut !== null) keep.add(comingOut);
  retainLinkedPassages(rec, keep);

  const reminders = rec.speech
    .map((isl, i) => ({ isl, i }))
    .filter(({ isl, i }) => isl.role === 'reminder' && !keep.has(i));

  if (reminders.length === 0) return retainLinkedPassages(rec, keep);

  const interval = REMINDER_INTERVAL_SEC[cadence];
  const want = interval === Infinity
    ? 0
    : Math.min(reminders.length, Math.max(0, Math.round(targetSec / interval) - 1));

  if (want >= reminders.length) {
    reminders.forEach(({ i }) => keep.add(i));
    return retainLinkedPassages(rec, keep);
  }

  // Spread `want` picks evenly across the reminders available, by their position in
  // the original recording rather than their index.
  const span = rec.nativeSec;
  for (let k = 0; k < want; k++) {
    const at = (span * (k + 1)) / (want + 1);
    let best = -1;
    let bestDist = Infinity;
    for (const { isl, i } of reminders) {
      if (keep.has(i)) continue;
      const d = Math.abs((isl.srcStart + isl.srcEnd) / 2 - at);
      if (d < bestDist) { bestDist = d; best = i; }
    }
    if (best >= 0) {
      keep.add(best);
      retainLinkedPassages(rec, keep);
    }
  }
  return retainLinkedPassages(rec, keep);
}

// ---------------------------------------------------------------- gap merging

interface EffectiveGap {
  /** Gaps from the source recording that this one spans. */
  sources: number[];
  nativeSec: number;
  minSec: number;
  maxSec: number;
}

/**
 * Dropping a reminder joins the sitting periods on either side of it into one.
 * Its bounds add, so the merged gap can be compressed as hard as both parts allowed
 * and stretched as far as both allowed. An omitted, approved whole reminder affects
 * proportional weighting without becoming a mandatory minimum.
 */
export function effectiveGaps(rec: Recording, keptIslands: number[]): EffectiveGap[] {
  const out: EffectiveGap[] = [];
  const linked = protectedTransitions(rec);
  const gapAfter = new Map<number, { gap: Gap; index: number }>();
  rec.gaps.forEach((gap, index) => gapAfter.set(gap.afterIsland, { gap, index }));

  // A playable gap is a connector between two retained passages. Source silence before
  // the first retained passage or after the last one has no such endpoints and must not
  // become a giant blank edge when a session is stretched. This was visible in the real
  // 30 March 2007 recording: a 60-minute preview ended its last speech at 23:41.
  for (let k = 0; k + 1 < keptIslands.length; k++) {
    const left = keptIslands[k];
    const right = keptIslands[k + 1];
    const connector: EffectiveGap = { sources: [], nativeSec: 0, minSec: 0, maxSec: 0 };
    for (let i = left; i < right; i++) {
      const found = gapAfter.get(i);
      if (found) {
        connector.sources.push(found.index);
        connector.nativeSec += found.gap.nativeSec;
        connector.minSec += found.gap.minSec;
        // Time belongs to practice gaps, not to the pauses separating successive
        // instructions. Preserve their original ceiling even for an extended sit.
        connector.maxSec += linked.has(i)
          ? Math.min(found.gap.maxSec, found.gap.nativeSec)
          : found.gap.maxSec;
      }
      // A cadence decision may omit a whole approved reminder. Its duration is useful
      // as a proportional weight, but it is not a new mandatory silence floor.
      if (i > left) connector.nativeSec += rec.speech[i].srcEnd - rec.speech[i].srcStart;
    }
    // Adjacent islands can have no explicit gap. Keep the connector so the plan can
    // still allocate a requested duration without losing positional correspondence.
    out.push(connector);
  }
  return out;
}

// ---------------------------------------------------------------- distribution

/**
 * Share `budget` seconds of silence across the gaps.
 *
 * Allocate in proportion to the original pauses, with one shared scale clamped by
 * each pause's own bounds. Solve that scale against the whole budget so mixed minimum
 * and maximum constraints cannot freeze a share that later needs to move again.
 */
export function distribute(gaps: EffectiveGap[], budget: number): number[] {
  if (!gaps.length) return [];
  const floor = gaps.reduce((sum, gap) => sum + gap.minSec, 0);
  const ceiling = gaps.reduce((sum, gap) => sum + gap.maxSec, 0);
  if (budget <= floor) return gaps.map((gap) => gap.minSec);
  if (budget >= ceiling) return gaps.map((gap) => gap.maxSec);

  // Freezing both lower and upper bounds in one proportional pass is incorrect:
  // [min 100, max 200, weight 1] + [min 0, max 50, weight 100] can spend 150s
  // from a 100s budget. Solve the monotone sum of clamped shares instead. Short
  // instructional ceilings make mixed constraints common, so this must be exact.
  const positiveWeights = gaps.map((gap) => gap.nativeSec).filter((weight) => weight > 0);
  const weights = gaps.map((gap) => positiveWeights.length ? Math.max(0, gap.nativeSec) : 1);
  const shares = (scale: number) => gaps.map((gap, i) =>
    Math.max(gap.minSec, Math.min(gap.maxSec, scale * weights[i])));
  let lo = 0;
  let hi = budget / Math.min(...(positiveWeights.length ? positiveWeights : [1]));
  for (let pass = 0; pass < 64; pass++) {
    const mid = lo + (hi - lo) / 2;
    const sum = shares(mid).reduce((a, b) => a + b, 0);
    if (sum < budget) lo = mid;
    else hi = mid;
  }
  const out = shares(lo + (hi - lo) / 2);
  // Clear rounding residue without violating any pause bound.
  let residual = budget - out.reduce((a, b) => a + b, 0);
  for (let i = 0; i < gaps.length && residual !== 0; i++) {
    const change = Math.max(gaps[i].minSec - out[i], Math.min(gaps[i].maxSec - out[i], residual));
    out[i] += change;
    residual -= change;
  }
  return out;
}

// ---------------------------------------------------------------- duration cues

/**
 * Decide whether the opening's spoken duration has been contradicted.
 *
 * He very often says how long the sit will be. If we hand someone 45 minutes after he
 * told them "a beautiful way of spending a whole half an hour", the illusion breaks on
 * the first sentence — so the cue is trimmed. Inside the band, it stays: hearing him
 * name the duration is part of what makes it feel like a real sit rather than an edit.
 */
export interface CueTrim { island: number; fromSec: number; toSec: number }

export class InvalidEditError extends Error {
  readonly recordingId: string;
  readonly detail: string;
  constructor(recordingId: string, detail: string) {
    super(`${recordingId}: invalid duration-cue edit (${detail})`);
    this.name = 'InvalidEditError';
    this.recordingId = recordingId;
    this.detail = detail;
  }
}

export class StructuralPlanError extends Error {
  readonly recordingId: string;
  readonly diagnostics: string[];
  constructor(recordingId: string, diagnostics: string[]) {
    super(`${recordingId}: invalid session structure (${diagnostics.join('; ')})`);
    this.name = 'StructuralPlanError';
    this.recordingId = recordingId;
    this.diagnostics = diagnostics;
  }
}

/** Validate and union cue exclusions once, before any duration arithmetic or rendering. */
export function normalizeEdits(rec: Recording, trims: readonly CueTrim[]): CueTrim[] {
  const valid: CueTrim[] = [];
  for (const trim of trims) {
    const isl = rec.speech[trim.island];
    if (!isl) throw new InvalidEditError(rec.id, `missing island ${trim.island}`);
    if (![trim.fromSec, trim.toSec].every(Number.isFinite)
        || trim.fromSec >= trim.toSec
        || trim.fromSec < isl.srcStart
        || trim.toSec > isl.srcEnd
        || (trim.fromSec === isl.srcStart && trim.toSec === isl.srcEnd)) {
      throw new InvalidEditError(rec.id, `range ${trim.fromSec}–${trim.toSec} outside island ${trim.island}`);
    }
    valid.push({ ...trim });
  }
  valid.sort((a, b) => a.island - b.island || a.fromSec - b.fromSec || a.toSec - b.toSec);
  const unioned: CueTrim[] = [];
  for (const trim of valid) {
    const prev = unioned.at(-1);
    if (prev && prev.island === trim.island && trim.fromSec <= prev.toSec) {
      prev.toSec = Math.max(prev.toSec, trim.toSec);
    } else unioned.push(trim);
  }
  for (const trim of unioned) {
    const isl = rec.speech[trim.island];
    if (trim.fromSec === isl.srcStart && trim.toSec === isl.srcEnd) {
      throw new InvalidEditError(rec.id, `range removes all of island ${trim.island}`);
    }
  }
  return unioned;
}

export function cueReviewFor(rec: Recording, cue: DurationCue) {
  const reviewed = CUE_REVIEWS[rec.id]?.find((entry) =>
    entry.cue.island === cue.island && entry.cue.atSec === cue.atSec);
  if (reviewed) {
    if (!Object.entries(reviewed.cue).every(([key, value]) =>
      cue[key as keyof DurationCue] === value)) {
      throw new InvalidEditError(rec.id, `stale duration-cue review at ${cue.atSec}`);
    }
    return reviewed;
  }
  return {
    action: cue.editReview?.status === 'approved' && cue.editReview.decisionId
      ? 'existing-cut' as const : 'retain' as const,
    band: undefined,
  };
}

export function cueTrimFor(rec: Recording, targetSec: number): CueTrim[] {
  const trims: CueTrim[] = [];
  for (const cue of rec.durationCues) {
    if (cue.precision === 'vague') continue;
    const review = cueReviewFor(rec, cue);
    if (review.action === 'ignore') continue;
    const [min, max] = review.band ?? [cue.bandMinSec, cue.bandMaxSec];
    if (targetSec >= min && targetSec <= max) continue;
    if (review.action === 'existing-cut') {
      trims.push({ island: cue.island, fromSec: cue.atSec, toSec: cue.endSec });
    }
  }
  return trims;
}

/** Contradicted cues that cannot be removed without losing their dependent instruction. */
export function retainedCueConflicts(rec: Recording, targetSec: number): CueTrim[] {
  return rec.durationCues.flatMap((cue) => {
    if (cue.precision === 'vague') return [];
    const review = cueReviewFor(rec, cue);
    const [min, max] = review.band ?? [cue.bandMinSec, cue.bandMaxSec];
    return review.action === 'retain' && (targetSec < min || targetSec > max)
      ? [{ island: cue.island, fromSec: cue.atSec, toSec: cue.endSec }] : [];
  });
}

interface PhaseSelection {
  bounds: Map<number, { srcStart: number; srcEnd: number }>;
  spokenSec: number;
  adjustment?: NonNullable<SessionPlan['preview']['speechRatioAdjustment']>;
}

function approvedPhaseSelection(
  rec: Recording, keptIdx: number[], targetSec: number, opts: PlanOptions,
  trims: CueTrim[], gaps: EffectiveGap[],
): PhaseSelection {
  const { desiredSpeechRatio } = opts;
  const bounds = new Map<number, { srcStart: number; srcEnd: number }>();
  if (desiredSpeechRatio !== undefined
      && (!Number.isFinite(desiredSpeechRatio) || desiredSpeechRatio <= 0 || desiredSpeechRatio > 1)) {
    throw new InvalidEditError(rec.id, `invalid desired speech ratio ${desiredSpeechRatio}`);
  }
  const first = keptIdx[0];
  const last = keptIdx.at(-1);
  const validate = (variant: ReviewedPhaseVariant, boundary: number | undefined, phase: string) => {
    const island = rec.speech[variant.island];
    if (variant.review?.status !== 'approved' || !variant.review.decisionId
        || variant.boundaryBasis !== 'complete-thought') {
      throw new InvalidEditError(rec.id, `${phase} variant ${variant.id} is not reviewed`);
    }
    if (variant.island !== boundary || !island
        || !Number.isFinite(variant.srcStart) || !Number.isFinite(variant.srcEnd)
        || variant.srcStart < island.srcStart || variant.srcEnd > island.srcEnd
        || variant.srcStart >= variant.srcEnd) {
      throw new InvalidEditError(rec.id, `${phase} variant ${variant.id} has invalid source bounds`);
    }
  };
  const openings = desiredSpeechRatio === undefined ? [] : rec.phaseVariants?.opening ?? [];
  const closings = desiredSpeechRatio === undefined ? [] : rec.phaseVariants?.closing ?? [];
  openings.forEach((v) => validate(v, first, 'opening'));
  closings.forEach((v) => validate(v, last, 'closing'));
  const conflicts = retainedCueConflicts(rec, targetSec);
  const linked = protectedTransitions(rec);
  const minGapSec = gaps.reduce((sum, gap) => sum + gap.minSec, 0);
  const maxGapSec = gaps.reduce((sum, gap) => sum + gap.maxSec, 0);
  const tolerance = opts.toleranceSec ?? 30;
  type Candidate = {
    opening?: ReviewedPhaseVariant; closing?: ReviewedPhaseVariant; spokenSec: number; reachable: boolean;
  };
  const openingChoices: Array<ReviewedPhaseVariant | undefined> = [undefined, ...openings];
  const closingChoices: Array<ReviewedPhaseVariant | undefined> = [undefined, ...closings];
  const candidates: Candidate[] = [];
  for (const opening of openingChoices) for (const closing of closingChoices) {
    // A one-island recording cannot take two conflicting boundary variants.
    if (opening && closing && opening.island === closing.island
        && (opening.srcStart !== closing.srcStart || opening.srcEnd !== closing.srcEnd)) continue;
    // Complete-thought approval inside one island does not attest that a dependent
    // passage outside it still has its setup. Keep the linked block intact.
    if ((opening && linked.has(opening.island))
        || (closing && linked.has(closing.island - 1))) continue;
    let spokenSec = 0;
    let compatible = true;
    for (const i of keptIdx) {
      const variant = opening?.island === i ? opening : closing?.island === i ? closing : undefined;
      const retained = variant ?? rec.speech[i];
      const overlaps = (trim: CueTrim) => trim.island === i
        && trim.toSec > retained.srcStart && trim.fromSec < retained.srcEnd;
      if (conflicts.some(overlaps)) { compatible = false; break; }
      const mine = trims.filter(overlaps);
      // Intersecting a sentence exclusion creates an unreviewed phrase-level cut.
      if (mine.some((trim) => trim.fromSec < retained.srcStart || trim.toSec > retained.srcEnd)) {
        compatible = false; break;
      }
      const duration = retained.srcEnd - retained.srcStart
        - mine.reduce((sum, trim) => sum + trim.toSec - trim.fromSec, 0);
      if (duration <= 0) { compatible = false; break; }
      spokenSec += duration;
    }
    if (!compatible) continue;
    candidates.push({ opening, closing, spokenSec,
      reachable: targetSec >= spokenSec + minGapSec - tolerance
        && targetSec <= spokenSec + maxGapSec + tolerance });
  }
  if (!candidates.length) {
    throw new InvalidEditError(rec.id, 'no complete, compatible speech range at the requested duration');
  }
  candidates.sort((a, b) => {
    if (a.reachable !== b.reachable) return a.reachable ? -1 : 1;
    const distance = Math.abs(a.spokenSec / targetSec - (desiredSpeechRatio ?? 1))
      - Math.abs(b.spokenSec / targetSec - (desiredSpeechRatio ?? 1));
    if (Math.abs(distance) > 1e-12) return distance;
    // At equal fit retain more of the source; IDs make the remaining tie deterministic.
    if (a.spokenSec !== b.spokenSec) return b.spokenSec - a.spokenSec;
    return `${a.opening?.id ?? ''}\0${a.closing?.id ?? ''}`
      .localeCompare(`${b.opening?.id ?? ''}\0${b.closing?.id ?? ''}`);
  });
  const chosen = candidates[0];
  if (chosen.opening) bounds.set(chosen.opening.island, chosen.opening);
  if (chosen.closing) bounds.set(chosen.closing.island, chosen.closing);
  const applied = !!chosen.opening || !!chosen.closing;
  return {
    bounds,
    spokenSec: chosen.spokenSec,
    adjustment: desiredSpeechRatio === undefined ? undefined : {
      requested: desiredSpeechRatio,
      applied,
      openingVariantId: chosen.opening?.id,
      closingVariantId: chosen.closing?.id,
      reason: applied ? 'closest-approved'
        : openings.length === 0 && closings.length === 0 ? 'no-approved-variants' : 'already-closest',
    },
  };
}

/**
 * Play an island, minus any sentences that have been contradicted.
 *
 * Returns one segment normally and several when a cue is cut out of the middle. The
 * exclusion needs its own review: an ASR segment can end halfway through a posture
 * instruction, and a sentence can introduce a metaphor needed by the following sentence.
 */
export function speechSegments(
  rec: Recording, islandIndex: number, at: number, trims: CueTrim[],
  retained?: { srcStart: number; srcEnd: number },
): { segs: Timed[]; end: number } {
  const isl = rec.speech[islandIndex];
  const srcStart = retained?.srcStart ?? isl.srcStart;
  const srcEnd = retained?.srcEnd ?? isl.srcEnd;
  const mine = trims
    .filter((t) => t.island === islandIndex
      && t.toSec > srcStart && t.fromSec < srcEnd)
    .map((t) => ({ ...t, fromSec: Math.max(t.fromSec, srcStart), toSec: Math.min(t.toSec, srcEnd) }))
    .sort((a, b) => a.fromSec - b.fromSec);

  if (mine.length === 0) {
    const dur = srcEnd - srcStart;
    return {
      segs: [{
        kind: 'speech', startSec: at, endSec: at + dur, island: islandIndex,
        ...(retained ? { srcStart, srcEnd } : {}),
      }],
      end: at + dur,
    };
  }

  const segs: Timed[] = [];
  let cursor = srcStart;
  let t = at;
  for (const trim of mine) {
    if (trim.fromSec > cursor) {
      const dur = trim.fromSec - cursor;
      segs.push({
        kind: 'speech', startSec: t, endSec: t + dur, island: islandIndex,
        srcStart: cursor, srcEnd: trim.fromSec,
      });
      t += dur;
    }
    cursor = Math.max(cursor, trim.toSec);
  }
  if (cursor < srcEnd) {
    const dur = srcEnd - cursor;
    segs.push({
      kind: 'speech', startSec: t, endSec: t + dur, island: islandIndex,
      srcStart: cursor, srcEnd,
    });
    t += dur;
  }
  return { segs, end: t };
}

// ---------------------------------------------------------------- the plan

function computeComposition(rec: Recording, targetSec: number, cadence: Cadence, opts: PlanOptions) {
  if (!Number.isFinite(targetSec) || targetSec <= 0
      || !Number.isFinite(opts.toleranceSec ?? 30) || (opts.toleranceSec ?? 30) < 0) {
    throw new InvalidEditError(rec.id, 'target and tolerance must be finite positive/nonnegative durations');
  }
  const keptIdx = selectIslands(rec, targetSec, cadence);
  if (!keptIdx.length) throw new InvalidEditError(rec.id, 'recording contains no speech');
  const trims = normalizeEdits(rec, cueTrimFor(rec, targetSec));
  const gaps = effectiveGaps(rec, keptIdx);
  const phases = approvedPhaseSelection(rec, keptIdx, targetSec, opts, trims, gaps);
  const floor = phases.spokenSec + gaps.reduce((sum, gap) => sum + gap.minSec, 0);
  const ceiling = phases.spokenSec + gaps.reduce((sum, gap) => sum + gap.maxSec, 0);
  return { keptIdx, trims, gaps, phases, floor, ceiling };
}

const compositionCache = new WeakMap<Recording, {
  source: ReturnType<typeof compositionSource>;
  requests: Map<string, ReturnType<typeof computeComposition> | InvalidEditError>;
}>();

function compositionSource(rec: Recording) {
  return {
    id: rec.id, nativeSec: rec.nativeSec,
    speech: rec.speech.map((s) => ({ srcStart: s.srcStart, srcEnd: s.srcEnd, role: s.role })),
    gaps: rec.gaps.map((g) => ({ afterIsland: g.afterIsland, nativeSec: g.nativeSec,
      minSec: g.minSec, maxSec: g.maxSec })),
    cues: rec.durationCues.map((c) => ({ ...c, editReview: c.editReview ? { ...c.editReview } : undefined })),
    variants: rec.phaseVariants ? JSON.stringify(rec.phaseVariants) : undefined,
    links: JSON.stringify(rec.linkDecisions),
  };
}

function sameCompositionSource(rec: Recording, source: ReturnType<typeof compositionSource>): boolean {
  if (rec.id !== source.id || rec.nativeSec !== source.nativeSec
      || rec.speech.length !== source.speech.length || rec.gaps.length !== source.gaps.length
      || rec.durationCues.length !== source.cues.length) return false;
  for (let i = 0; i < rec.speech.length; i++) {
    const s = rec.speech[i], previous = source.speech[i];
    if (s.srcStart !== previous.srcStart || s.srcEnd !== previous.srcEnd || s.role !== previous.role) return false;
  }
  for (let i = 0; i < rec.gaps.length; i++) {
    const g = rec.gaps[i], previous = source.gaps[i];
    if (g.afterIsland !== previous.afterIsland || g.nativeSec !== previous.nativeSec
        || g.minSec !== previous.minSec || g.maxSec !== previous.maxSec) return false;
  }
  for (let i = 0; i < rec.durationCues.length; i++) {
    const c = rec.durationCues[i], previous = source.cues[i];
    if (c.island !== previous.island || c.atSec !== previous.atSec || c.endSec !== previous.endSec
        || c.text !== previous.text || c.precision !== previous.precision || c.statedSec !== previous.statedSec
        || c.bandMinSec !== previous.bandMinSec || c.bandMaxSec !== previous.bandMaxSec
        || c.editReview?.status !== previous.editReview?.status
        || c.editReview?.decisionId !== previous.editReview?.decisionId) return false;
  }
  return (rec.phaseVariants ? JSON.stringify(rec.phaseVariants) : undefined) === source.variants
    && JSON.stringify(rec.linkDecisions) === source.links;
}

function prepareComposition(rec: Recording, targetSec: number, cadence: Cadence, opts: PlanOptions) {
  // Selection evaluates the same corpus repeatedly; recomputing context groups for
  // every rotation is expensive. Bind the small cache to every planning input so a
  // mutated recording, revised cue or variant cannot reuse an older composition.
  let cache = compositionCache.get(rec);
  if (!cache || !sameCompositionSource(rec, cache.source)) {
    cache = { source: compositionSource(rec), requests: new Map() };
    compositionCache.set(rec, cache);
  }
  const key = `${targetSec}/${cadence}/${opts.toleranceSec ?? 30}/${opts.desiredSpeechRatio ?? ''}`;
  const cached = cache.requests.get(key);
  if (cached instanceof InvalidEditError) throw cached;
  if (cached) return cached;
  // Deadline previews can visit a new target every second. Bound per-recording memory.
  if (cache.requests.size >= 8) cache.requests.delete(cache.requests.keys().next().value!);
  try {
    const prepared = computeComposition(rec, targetSec, cadence, opts);
    cache.requests.set(key, prepared);
    return prepared;
  } catch (error) {
    // Repeated selection must not rebuild thousands of identical rejection stacks.
    if (error instanceof InvalidEditError) cache.requests.set(key, error);
    throw error;
  }
}

export function planSession(
  rec: Recording,
  targetSec: number,
  cadence: Cadence,
  opts: PlanOptions = {},
): SessionPlan {
  const tolerance = opts.toleranceSec ?? 30;

  const { keptIdx, trims, gaps, phases, floor, ceiling } = prepareComposition(rec, targetSec, cadence, opts);
  const kept: SpeechIsland[] = keptIdx.map((i) => rec.speech[i]);
  const spokenSec = phases.spokenSec;
  const trimmedSec = trims
    .filter((tr) => keptIdx.includes(tr.island))
    .reduce((sum, tr) => {
      const island = rec.speech[tr.island];
      const retained = phases.bounds.get(tr.island) ?? island;
      return sum + Math.max(0,
        Math.min(tr.toSec, retained.srcEnd) - Math.max(tr.fromSec, retained.srcStart));
    }, 0);

  if (targetSec < floor - tolerance || targetSec > ceiling + tolerance) {
    throw new UnreachableError(rec.id, targetSec, { minSec: floor, maxSec: ceiling });
  }

  const budget = Math.max(0, targetSec - spokenSec);
  const lengths = distribute(gaps, budget);

  // Walk retained speech with exactly one connector between each adjacent pair. The
  // opening is anchored at zero and the closing at the requested end by construction.
  const timeline: Timed[] = [];
  let t = 0;
  const push = (seg: Timed) => { if (seg.endSec > seg.startSec) timeline.push(seg); };

  let gi = 0;

  keptIdx.forEach((islandIndex, n) => {
    const { segs, end } = speechSegments(rec, islandIndex, t, trims, phases.bounds.get(islandIndex));
    segs.forEach(push);
    t = end;
    if (gi < gaps.length && n < keptIdx.length - 1) {
      push({ kind: 'tone', startSec: t, endSec: t + lengths[gi], gap: gi });
      t += lengths[gi];
      gi++;
    }
  });

  const actualSec = t;
  const longestSilenceSec = timeline.reduce(
    (m, s) => (s.kind === 'tone' ? Math.max(m, s.endSec - s.startSec) : m), 0);

  const plan: SessionPlan = {
    recordingId: rec.id,
    targetSec,
    actualSec,
    cadence,
    timeline,
    preview: {
      spokenSec,
      reminderCount: kept.filter((i) => i.role === 'reminder').length,
      longestSilenceSec,
      cueTrimmed: trimmedSec > 0,
      speechRatioAdjustment: phases.adjustment,
    },
  };
  const diagnostics = planDiagnostics(plan, rec);
  if (diagnostics.violations.length) throw new StructuralPlanError(rec.id, diagnostics.violations);
  return plan;
}

export interface PlanDiagnostics {
  firstSpeechSec: number | null;
  lastSpeechSec: number | null;
  timelineSec: number;
  sourceSpeechSec: number;
  violations: string[];
}

/** Cheap structural evidence carried by every preview/start evaluation. */
export function planDiagnostics(plan: SessionPlan, rec: Recording): PlanDiagnostics {
  const speech = plan.timeline.filter((seg): seg is Extract<Timed, { kind: 'speech' }> =>
    seg.kind === 'speech');
  const violations: string[] = [];
  const firstSpeechSec = speech[0]?.startSec ?? null;
  const lastSpeechSec = speech.at(-1)?.endSec ?? null;
  const timelineSec = plan.timeline.reduce((sum, seg) => sum + seg.endSec - seg.startSec, 0);
  let previousSourceEnd = -Infinity;
  const sourceSpeechSec = speech.reduce((sum, seg) => {
    const island = rec.speech[seg.island];
    if (!island) {
      violations.push(`timeline references missing island ${seg.island}`);
      return sum;
    }
    const from = seg.srcStart ?? island.srcStart;
    const to = seg.srcEnd ?? island.srcEnd;
    if (from < island.srcStart || to > island.srcEnd || from >= to) {
      violations.push(`speech ${seg.island} exceeds its source island`);
    }
    if (from < previousSourceEnd - 1e-6) violations.push(`speech ${seg.island} repeats or reorders source`);
    previousSourceEnd = Math.max(previousSourceEnd, to);
    return sum + Math.max(0, to - from);
  }, 0);
  for (let i = 0; i < plan.timeline.length; i++) {
    const seg = plan.timeline[i];
    if (!Number.isFinite(seg.startSec) || !Number.isFinite(seg.endSec) || seg.endSec <= seg.startSec) {
      violations.push(`segment ${i} has invalid bounds`);
    }
    if (i === 0 && Math.abs(seg.startSec) > 1e-6) violations.push('timeline does not start at zero');
    if (i > 0 && Math.abs(seg.startSec - plan.timeline[i - 1].endSec) > 1e-6) {
      violations.push(`timeline discontinuity before segment ${i}`);
    }
  }
  if (firstSpeechSec === null) violations.push('plan contains no speech');
  else if (Math.abs(firstSpeechSec) > 1e-6) violations.push('opening speech is not anchored at zero');
  if (lastSpeechSec === null || Math.abs(lastSpeechSec - plan.actualSec) > 1e-6) {
    violations.push('closing speech is not anchored at the session end');
  }
  if (Math.abs(timelineSec - plan.actualSec) > 1e-6) violations.push('segment durations do not equal actualSec');
  if (Math.abs(sourceSpeechSec - plan.preview.spokenSec) > 1e-6) {
    violations.push('source speech coverage does not equal preview spokenSec');
  }
  return { firstSpeechSec, lastSpeechSec, timelineSec, sourceSpeechSec, violations };
}

/** Reachable bounds using the same cadence, cue normalization and connector model as planning. */
export function sessionReach(
  rec: Recording, targetSec: number, cadence: Cadence,
  opts: PlanOptions = {},
): SessionReach {
  const { floor, ceiling } = prepareComposition(rec, targetSec, cadence, opts);
  return { minSec: floor, maxSec: ceiling };
}

/** Can this recording be asked for this length at all? Cheap enough to call per card. */
/**
 * Can this recording be made this long?
 *
 * The tolerance is a parameter because the answer depends on what was promised. A
 * preset chip saying "25 minutes" is a round number and 30 seconds either way is
 * invisible. A finish time of 07:30 is a promise about a clock, and the same 30 seconds
 * makes it a lie — so the deadline control asks with toleranceSec: 0.
 */
export function canReach(
  rec: Recording, targetSec: number, cadence: Cadence, toleranceSec = 30,
  opts: Pick<PlanOptions, 'desiredSpeechRatio'> = {},
): boolean {
  try {
    const reach = sessionReach(rec, targetSec, cadence, { ...opts, toleranceSec });
    return targetSec >= reach.minSec - toleranceSec && targetSec <= reach.maxSec + toleranceSec;
  } catch (error) {
    if (error instanceof InvalidEditError) return false;
    throw error;
  }
}
